<script setup lang="ts">
import { Head, useForm } from "@inertiajs/vue3"
import { computed, ref, watch } from "vue"
import PageHeading from "@/Components/Headings/PageHeading.vue"
import { PageHeading as PageHeadingTS } from "@/types/PageHeading"
import Button from "@/Components/Utils/Button.vue"
import PriceTiers from "@/Pages/Kasir/Products/PriceTiers.vue"
import PureSelectInfiniteScroll from "@/Components/Pure/PureSelectInfiniteScroll.vue"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import axios from "axios"
import { debounce } from "lodash"

interface PriceTier {
    min_quantity: number
    price: number
}

interface SalesProduct {
    id: number
    code: string
    name: string
}

interface CategoryOption {
    id: number
    name: string
    code: string | null
}

interface Variation {
    id?: number
    name: string
    detail: string | null
    prices: PriceTier[]
}

const props = defineProps<{
    title?: string
    pageHead: PageHeadingTS
    initialCategory: CategoryOption | null
    printTypes: { value: string; label: string }[]
    product: {
        product_category_id: number | null
        sku: string
        name: string
        print_type: string
        is_general: boolean
        manufacture_name: string | null
        description: string | null
        variations: Variation[]
        prices: PriceTier[]
    } | null
    defaultIsGeneral?: boolean
    submitRoute: { name: string; parameters?: any[]; method?: string }
}>()

const form = useForm({
    product_category_id: props.product?.product_category_id ?? null,
    sku: props.product?.sku ?? "",
    name: props.product?.name ?? "",
    print_type: props.product?.print_type ?? props.printTypes[0]?.value,
    is_general: props.product?.is_general ?? props.defaultIsGeneral ?? false,
    manufacture_name: props.product?.manufacture_name ?? "",
    description: props.product?.description ?? "",
    variations: (props.product?.variations ?? []) as Variation[],
    prices: (props.product?.prices?.length
        ? props.product.prices
        : [{ min_quantity: 0, price: 0 }]) as PriceTier[],
})

const selectedCategory = ref<CategoryOption | null>(props.initialCategory)

const onCategoryChange = (category: Record<string, any> | null) => {
    selectedCategory.value = category as CategoryOption | null
    clearError("product_category_id")
}
const categoryPrefix = computed(() =>
    selectedCategory.value?.code ? `${selectedCategory.value.code}-` : ""
)
const needsManufactureName = computed(() => form.print_type === "ditakurnia")

const clearError = (field: string) => form.clearErrors(field as any)

// The SKU is stored whole; the category code is only offered as a starting point.
const applyCategoryPrefix = () => {
    if (!categoryPrefix.value) return
    if (form.sku.startsWith(categoryPrefix.value)) return
    form.sku = sanitizeSku(categoryPrefix.value + form.sku)
    clearError("sku")
}

const sanitizeSku = (value: string) => value.replace(/[^A-Za-z0-9_-]/g, "")

const onSkuInput = (event: Event) => {
    form.sku = sanitizeSku((event.target as HTMLInputElement).value)
    clearError("sku")
}

const salesProducts = ref<SalesProduct[]>([])
const isSalesProductListOpen = ref(false)
const isFetchingSalesProducts = ref(false)
const highlightedIndex = ref(-1)
let latestSalesProductRequest = 0

const fetchSalesProducts = async () => {
    const requestId = ++latestSalesProductRequest
    isFetchingSalesProducts.value = true

    try {
        const search = form.manufacture_name?.trim()
        const response = await axios.get(
            route("kasir.products.sales-product-names", search ? { search } : {})
        )

        if (requestId !== latestSalesProductRequest) return
        salesProducts.value = response.data.data
        highlightedIndex.value = -1
    } catch (error) {
        if (requestId !== latestSalesProductRequest) return
        salesProducts.value = []
    }

    isFetchingSalesProducts.value = false
}

const debouncedFetchSalesProducts = debounce(fetchSalesProducts, 300)

const openSalesProductList = () => {
    isSalesProductListOpen.value = true
    fetchSalesProducts()
}

const closeSalesProductList = () => {
    debouncedFetchSalesProducts.cancel()
    isSalesProductListOpen.value = false
    highlightedIndex.value = -1
}

const onManufactureNameInput = () => {
    clearError("manufacture_name")
    isSalesProductListOpen.value = true
    isFetchingSalesProducts.value = true
    debouncedFetchSalesProducts()
}

const selectSalesProduct = (salesProduct: SalesProduct) => {
    form.manufacture_name = salesProduct.name
    clearError("manufacture_name")
    closeSalesProductList()
}

const moveHighlight = (step: number) => {
    if (!isSalesProductListOpen.value) {
        openSalesProductList()
        return
    }
    if (!salesProducts.value.length) return

    const total = salesProducts.value.length
    highlightedIndex.value = (highlightedIndex.value + step + total) % total
}

const onManufactureNameEnter = (event: KeyboardEvent) => {
    const highlighted = salesProducts.value[highlightedIndex.value]
    if (!isSalesProductListOpen.value || !highlighted) return

    event.preventDefault()
    selectSalesProduct(highlighted)
}

watch(
    () => form.print_type,
    (value) => {
        if (value !== "ditakurnia") form.manufacture_name = ""
    }
)

const addVariation = () =>
    form.variations.push({
        name: "",
        detail: "",
        prices: form.prices.map((tier) => ({ ...tier })),
    })
const removeVariation = (index: number) => form.variations.splice(index, 1)

const isVariationEmpty = (variation: Variation) =>
    !variation.name?.trim() && !variation.detail?.trim()

const variationError = (index: number) =>
    form.errors[`variations.${index}.name` as keyof typeof form.errors] as
    | string
    | undefined

const isLoading = ref(false)
const submit = () => {
    form.variations = form.variations.filter((variation) => !isVariationEmpty(variation))

    form[props.submitRoute.method ?? "post"](
        route(props.submitRoute.name, props.submitRoute.parameters),
        {
            onStart: () => (isLoading.value = true),
            onFinish: () => (isLoading.value = false),
        }
    )
}
</script>

<template>

    <Head :title="title" />
    <PageHeading :data="pageHead" />

    <div class="mx-auto max-w-4xl px-4 py-6">
        <section class="rounded-xl border border-gray-200 bg-white p-6">
            <h3 class="mb-4 font-semibold text-gray-700">Data produk</h3>

            <div class="grid gap-4 md:grid-cols-2">
                <div>
                    <label class="mb-1 block text-sm text-gray-600">
                        Kategori
                        <span v-if="selectedCategory?.code" class="font-mono text-xs text-gray-400">({{
                            selectedCategory.code }})</span>
                    </label>
                    <PureSelectInfiniteScroll
                        v-model="form.product_category_id"
                        :fetchRoute="{ name: 'kasir.categories.options' }"
                        optionLabel="name"
                        optionValue="id"
                        :initialOption="initialCategory"
                        placeholder="Tanpa kategori"
                        searchPlaceholder="Cari nama atau kode kategori"
                        emptyText="Kategori tidak ditemukan"
                        showClear
                        :invalid="!!form.errors.product_category_id"
                        :class="form.errors.product_category_id ? 'errorShake' : ''"
                        v-tooltip="'Kode kategori bisa dipakai sebagai awalan SKU'"
                        @change="onCategoryChange"
                    >
                        <template #option="{ option }">
                            <span class="flex w-full items-center justify-between gap-x-3">
                                <span>{{ option.name }}</span>
                                <span v-if="option.code" class="shrink-0 font-mono text-xs text-gray-400">{{ option.code }}</span>
                            </span>
                        </template>
                    </PureSelectInfiniteScroll>
                    <div v-if="form.errors.product_category_id" class="mt-1 text-xs text-red-500">
                        {{ form.errors.product_category_id }}
                    </div>
                </div>

                <div>
                    <label class="mb-1 block text-sm text-gray-600">Kode SKU <span class="text-red-500">*</span></label>
                    <div class="flex gap-x-2">
                        <input :value="form.sku" maxlength="30" placeholder="P-Box-SML"
                            class="w-full rounded-md border-gray-300 font-mono text-sm"
                            :class="form.errors.sku ? 'errorShake' : ''" @input="onSkuInput" />
                        <Button v-if="categoryPrefix" size="xs" type="tertiary" :label="categoryPrefix"
                            @click="applyCategoryPrefix" />
                    </div>
                    <div class="mt-1 text-xs text-gray-400">
                        Maks 30 karakter. Hanya huruf, angka, tanda hubung (-), dan garis bawah (_).
                    </div>
                    <div v-if="form.errors.sku" class="mt-1 text-xs text-red-500">
                        {{ form.errors.sku }}
                    </div>
                </div>

                <div class="grid md:col-span-2 md:grid-cols-2 gap-4">
                    <div>
                        <label class="mb-1 block text-sm text-gray-600">Nama produk <span
                                class="text-red-500">*</span></label>
                        <input v-model="form.name" placeholder="Paperbox Small"
                            class="w-full rounded-md border-gray-300 text-sm" :class="form.errors.name ? 'errorShake' : ''"
                            @input="clearError('name')" />
                        <div v-if="form.errors.name" class="mt-1 text-xs text-red-500">
                            {{ form.errors.name }}
                        </div>
                    </div>
                    <div v-if="needsManufactureName">
                        <label class="mb-1 block text-sm text-gray-600">Nama produk manufacture</label>
                        <div class="relative">
                            <input v-model="form.manufacture_name" placeholder="Ivory_Paper Box Medium"
                                autocomplete="off" class="w-full rounded-md border-gray-300 text-sm"
                                :class="form.errors.manufacture_name ? 'errorShake' : ''"
                                @input="onManufactureNameInput" @focus="openSalesProductList"
                                @blur="closeSalesProductList" @keydown.down.prevent="moveHighlight(1)"
                                @keydown.up.prevent="moveHighlight(-1)" @keydown.enter="onManufactureNameEnter"
                                @keydown.esc="closeSalesProductList" />

                            <ul v-if="isSalesProductListOpen"
                                class="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg">
                                <li v-if="isFetchingSalesProducts" class="px-3 py-2 text-gray-400">
                                    Mencari produk Sales...
                                </li>
                                <li v-else-if="!salesProducts.length" class="px-3 py-2 text-gray-400">
                                    Tidak ada produk Sales yang cocok, nama akan disimpan sesuai ketikan.
                                </li>
                                <li v-for="(salesProduct, index) in salesProducts" v-else :key="salesProduct.id"
                                    class="flex cursor-pointer items-center justify-between gap-x-3 px-3 py-2"
                                    :class="index === highlightedIndex ? 'bg-gray-100' : 'hover:bg-gray-50'"
                                    @mousedown.prevent="selectSalesProduct(salesProduct)"
                                    @mouseenter="highlightedIndex = index">
                                    <span class="text-gray-700">{{ salesProduct.name }}</span>
                                    <span class="font-mono text-xs text-gray-400">{{ salesProduct.code }}</span>
                                </li>
                            </ul>
                        </div>
                        <div class="mt-1 text-xs text-gray-400">
                            Nama sekunder yang dipakai bagian produksi. Ketik manual atau pilih dari produk Sales.
                        </div>
                        <div v-if="form.errors.manufacture_name" class="mt-1 text-xs text-red-500">
                            {{ form.errors.manufacture_name }}
                        </div>
                    </div>
                </div>

                <div>
                    <label class="mb-1 block text-sm text-gray-600">Jenis cetak <span
                            class="text-red-500">*</span></label>
                    <select v-model="form.print_type" class="w-full rounded-md border-gray-300 text-sm">
                        <option v-for="printType in printTypes" :key="printType.value" :value="printType.value">
                            {{ printType.label }}
                        </option>
                    </select>
                </div>

                <div>
                    <label class="flex items-start gap-x-2 text-sm text-gray-700 md:mt-6">
                        <input v-model="form.is_general" type="checkbox" class="mt-0.5 rounded border-gray-300" />
                        <span>
                            Produk Umum (tampilkan di semua brand)
                            <span class="block text-xs text-gray-400">
                                Stoknya dipakai bersama semua brand. Di Pesanan Kasir selalu diambil dari stok, tidak membuat Manufaktur.
                                <template v-if="form.print_type === 'ditakurnia'">Stok diisi lewat produksi stok di Manufaktur.</template>
                                <template v-else>Stok diisi lewat input stok / stok opname.</template>
                                <a :href="route('guide.public', 'produk-umum')" target="_blank" rel="noopener" class="text-indigo-600 underline" @click.stop>Panduan</a>
                            </span>
                        </span>
                    </label>
                </div>

                <div class="md:col-span-2">
                    <label class="mb-1 block text-sm text-gray-600">Deskripsi</label>
                    <textarea v-model="form.description" rows="2" class="w-full rounded-md border-gray-300 text-sm" />
                </div>
            </div>
        </section>

        <section class="mt-4 rounded-xl border border-gray-200 bg-white p-6">
            <div class="mb-4">
                <h3 class="font-semibold text-gray-700">Harga varian default</h3>
                <p class="text-xs text-gray-400">
                    Varian default adalah produk itu sendiri, jadi tidak bisa dihapus. Harga dipilih
                    berdasarkan jumlah pesanan terbesar yang memenuhi.
                </p>
            </div>

            <PriceTiers :form="form" :tiers="form.prices" error-prefix="prices" />
        </section>

        <section class="mt-4 rounded-xl border border-gray-200 bg-white p-6">
            <div class="mb-4 flex items-center justify-between">
                <div>
                    <h3 class="font-semibold text-gray-700">Variasi</h3>
                    <p class="text-xs text-gray-400">
                        Ukuran, bahan, area cetak, addon, dll. Setiap variasi punya harganya sendiri,
                        tidak mengikuti harga varian default.
                    </p>
                </div>
                <Button size="xs" type="secondary" icon="fal fa-plus" label="Tambah variasi" @click="addVariation" />
            </div>

            <div v-if="!form.variations.length"
                class="rounded-lg border border-dashed border-gray-200 py-6 text-center text-sm text-gray-400">
                Belum ada variasi selain default
            </div>

            <div v-else class="flex flex-col gap-4">
                <div v-for="(variation, index) in form.variations" :key="`variation-${index}`"
                    class="rounded-lg border border-gray-200 p-4">
                    <div class="flex items-start gap-x-3">
                        <div class="w-1/3">
                            <label class="mb-1 block text-xs text-gray-500">Nama variasi</label>
                            <input v-model="variation.name" placeholder="Ukuran Large"
                                class="w-full rounded-md border-gray-300 text-sm"
                                :class="variationError(index) ? 'errorShake' : ''"
                                @input="clearError(`variations.${index}.name`)" />
                            <div v-if="variationError(index)" class="mt-1 text-xs text-red-500">
                                {{ variationError(index) }}
                            </div>
                        </div>
                        <div class="flex-1">
                            <label class="mb-1 block text-xs text-gray-500">Detail</label>
                            <input v-model="variation.detail" placeholder="20 x 20 x 10 cm, Ivory 350gsm"
                                class="w-full rounded-md border-gray-300 text-sm" />
                        </div>
                        <button type="button" class="mt-6 pb-2 text-gray-400 hover:text-red-500"
                            @click="removeVariation(index)">
                            <FontAwesomeIcon icon="fal fa-times" fixed-width aria-hidden="true" />
                        </button>
                    </div>

                    <div class="mt-4 border-t border-gray-100 pt-4">
                        <div class="mb-2 text-xs font-semibold uppercase text-gray-400">
                            Harga variasi ini
                        </div>
                        <PriceTiers :form="form" :tiers="variation.prices"
                            :error-prefix="`variations.${index}.prices`" />
                    </div>
                </div>
            </div>
        </section>

        <div class="mt-4 flex justify-end">
            <Button type="save" :loading="isLoading || form.processing" label="Simpan" @click="submit" />
        </div>
    </div>
</template>
